/**
 * ============================================================================
 * useSupplierDetailQuery — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: TanStack Query hook for one supplier's full dossier — header,
 *          timeline, KPIs (with 12-month sparkline series), risk assessment
 *          history, audits, CAPAs, vendor evaluations, and supplier
 *          documents.
 * Endpoint: GET /api/v1/suppliers/{id}
 *
 * NOTE on payloads: per-event `payload` and per-evaluation `scorecard` are
 *  modeled as `Record<string, unknown>`. Narrow at the call site.
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import type {
  RiskCategory,
  SupplierCriticality,
  SupplierLifecycleStatus,
  SupplierSubtype,
} from '../SupplierWorkbench/useSuppliersQuery';

export type SupplierEventKind =
  | 'CREATED'
  | 'QUESTIONNAIRE_ISSUED'
  | 'QUESTIONNAIRE_RECEIVED'
  | 'AUDIT_SCHEDULED'
  | 'AUDIT_COMPLETED'
  | 'RISK_ASSESSED'
  | 'EVALUATION_SIGNED'
  | 'LIFECYCLE_TRANSITION'
  | 'DOCUMENT_LINKED'
  | 'CAPA_OPENED'
  | 'CAPA_CLOSED'
  | 'DISQUALIFIED'
  | 'OTHER';

export interface SupplierTimelineEvent {
  event_id: string;
  ts_utc: string;
  kind: SupplierEventKind;
  actor: string | null;
  title: string;
  description: string;
  /** Per-event extras. */
  payload: Record<string, unknown>;
}

export type SupplierKpiKey =
  | 'OTIF'
  | 'DEVIATION_COUNT'
  | 'COMPLAINT_COUNT'
  | 'OOS_RATE'
  | 'AUDIT_SCORE'
  | 'CAPA_EFFECTIVENESS'
  | 'DELIVERY_PERFORMANCE'
  | 'QUALITY_INCIDENTS';

export interface SupplierKpiTrendPoint {
  ts: string;
  value: number;
}

export interface SupplierKpi {
  key: SupplierKpiKey;
  label: string;
  value: number | null;
  unit_kind: 'COUNT' | 'PERCENTAGE' | 'DAYS' | 'SCORE';
  band: 'GREEN' | 'AMBER' | 'RED' | 'UNKNOWN';
  trend: SupplierKpiTrendPoint[];
}

export interface RiskAssessmentRow {
  assessment_id: string;
  ts_utc: string;
  formula_version: string;
  inputs: Record<string, unknown>;
  computed_score: number;
  category: RiskCategory;
  rationale: string;
  signer: string | null;
}

export type AuditOutcome = 'PASS' | 'PASS_WITH_FINDINGS' | 'FAIL';
export type AuditStatus =
  | 'PLANNED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'CANCELLED';

export interface AuditRow {
  audit_id: string;
  scheduled_for_utc: string;
  completed_at_utc: string | null;
  status: AuditStatus;
  outcome: AuditOutcome | null;
  auditor: string;
  /** Convenience flag: server flags conflicts of interest (SUP-SoD-001). */
  has_sod_conflict: boolean;
}

export type CapaSeverity = 'MINOR' | 'MAJOR' | 'CRITICAL';
export type CapaStatus =
  | 'OPEN'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'VERIFIED'
  | 'CLOSED';

export interface CapaRow {
  capa_id: string;
  opened_at_utc: string;
  severity: CapaSeverity;
  status: CapaStatus;
  title: string;
  /** Where the EV cohort sits in the verification window. */
  effectiveness_cohort: 'PENDING' | 'IN_WINDOW' | 'PASSED' | 'FAILED' | null;
}

export type EvaluationStatus =
  | 'DRAFT'
  | 'IN_REVIEW'
  | 'SIGNED_APPROVED'
  | 'SIGNED_CONDITIONAL'
  | 'SIGNED_DISQUALIFIED';

export interface VendorEvaluationRow {
  evaluation_id: string;
  period_start_utc: string;
  period_end_utc: string;
  status: EvaluationStatus;
  weighted_score: number | null;
  classification: 'APPROVED' | 'CONDITIONAL' | 'DISQUALIFIED' | null;
  scorecard: Record<string, unknown>;
}

export type SupplierDocKind =
  | 'SUPPLIER_QUESTIONNAIRE'
  | 'SUPPLIER_AUDIT_REPORT'
  | 'QUALITY_AGREEMENT'
  | 'CERTIFICATE_OF_ANALYSIS'
  | 'CERTIFICATE_GMP'
  | 'CERTIFICATE_ISO'
  | 'OTHER';

export interface SupplierDocRow {
  document_id: string;
  kind: SupplierDocKind;
  title: string;
  effective_at_utc: string | null;
  expires_at_utc: string | null;
  state: string;
}

// ---- Vendor Qualification System (VQ2–VQ5) consolidated dossier rows ----
export interface RequalificationRow {
  requalification_id: string;
  cycle_label: string;
  trigger: string;
  risk_category_at_cycle: string | null;
  frequency_months: number | null;
  due_at_utc: string;
  performed_at_utc: string | null;
  next_due_at_utc: string | null;
  outcome: string | null;
  status: 'COMPLETED' | 'OVERDUE' | 'DUE' | 'SCHEDULED';
  performance_summary: string | null;
  days_to_due: number | null;
}

export interface AssessmentSection {
  key: string;
  label: string;
  score: number;
  max: number;
  notes?: string;
}

export interface AssessmentRow {
  assessment_id: string;
  status: string;
  version: string;
  assessment_type: string | null;
  overall_score: number | null;
  recommendation: string | null;
  sections: AssessmentSection[];
  issued_at_utc: string | null;
  reviewed_at_utc: string | null;
}

export interface AuditPlanRow {
  plan_entry_id: string;
  plan_year: number;
  audit_type: string;
  risk_category_basis: string | null;
  planned_at_utc: string;
  status: 'PLANNED' | 'SCHEDULED' | 'COMPLETED' | 'OVERDUE' | 'WAIVED';
  executed_audit_id: string | null;
  audit?: { outcome: string | null; outcome_score: number | null; completed_at_utc: string | null } | null;
  findings_count?: number;
  capa_links?: string[];
  days_to_planned: number | null;
}

export interface ConditionalApprovalRow {
  approval_id: string;
  approval_type: 'CONDITIONAL' | 'PROVISIONAL';
  decided_at_utc: string;
  approver_authority: string | null;
  incomplete_records: string;
  justification: string;
  conditions: string;
  review_by_utc: string | null;
  status: 'OPEN' | 'SATISFIED' | 'REVOKED' | 'EXPIRED';
  condition_overdue: boolean;
  closure_note: string | null;
  linked_capa_id: string | null;
  linked_audit_id: string | null;
}

export interface SupplierDetail {
  supplier_id: string;
  name: string;
  legal_name: string | null;
  criticality: SupplierCriticality;
  subtype: SupplierSubtype;
  lifecycle_status: SupplierLifecycleStatus;
  risk_score: number | null;
  risk_category: RiskCategory | null;
  next_requalification_at_utc: string | null;
  // SUP-FULL — identity & contact (optional so older payloads degrade gracefully).
  country?: string | null;
  tax_id?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  single_source?: boolean;
  aggregate_quality_score: number | null;
  timeline: SupplierTimelineEvent[];
  kpis: SupplierKpi[];
  risk_assessments: RiskAssessmentRow[];
  audits: AuditRow[];
  capas: CapaRow[];
  evaluations: VendorEvaluationRow[];
  documents: SupplierDocRow[];
  /** VQ2–VQ5 — optional so older payloads degrade gracefully. */
  requalifications?: RequalificationRow[];
  assessments?: AssessmentRow[];
  audit_plan?: AuditPlanRow[];
  conditional_approvals?: ConditionalApprovalRow[];
}

export function useSupplierDetailQuery(
  supplierId: string,
): UseQueryResult<SupplierDetail> {
  return useQuery<SupplierDetail>({
    queryKey: ['suppliers', supplierId, 'detail'],
    enabled: !!supplierId,
    queryFn: ({ signal }) =>
      apiFetch<SupplierDetail>(
        `/api/v1/suppliers/${encodeURIComponent(supplierId)}`,
        { signal },
      ),
  });
}
